import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { Package, MapPin, Gauge } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { PageHeader, LoadingState, StatusBadge } from '@/components/common';
import { DetailLayout, DetailList, RailCard, StatStrip } from '@/components/common/DetailPanels';
import { consumptionApi, type ConsumptionRecord } from '@/api/services/consumption.api';
import { formatDateTime, formatCurrency, formatNumber, toTitleCase } from '@/lib/utils';
import { getErrorMessage } from '@/api/client';

/**
 * One consumption record — what was drawn, against which job, and by how much
 * it differed from plan.
 *
 * This existed as a sheet on Consumption Tracking and as a dialog on
 * Consumption Approvals: the same record, two shapes, depending on where you
 * came from. Approving a variance is a hand-off between people, which makes it
 * exactly the case where somebody needs to send a link.
 */
export default function ConsumptionRecordDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<ConsumptionRecord | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    consumptionApi
      .getById(id)
      .then((res) => !cancelled && setRecord(res.data))
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        navigate('/consumption');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading) return <LoadingState variant="detail" />;
  if (!record) return null;

  const variance = record.variancePercentage;
  const returned = record.returnedQuantity ?? 0;
  const net = record.quantity - returned;

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Consumption', href: '/consumption' },
            { label: record.item.sku },
          ]}
          title={record.item.name}
          description={`${formatNumber(record.quantity)} ${record.unit} on ${formatDateTime(record.consumedAt)}`}
          titleAdornment={
            <StatusBadge
              status={record.approvalStatus.toLowerCase()}
              label={toTitleCase(record.approvalStatus)}
            />
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <DetailLayout
          rail={
            <>
              <RailCard title="Item" icon={<Package className="h-4 w-4 text-muted-foreground" />}>
                <DetailList
                  items={[
                    {
                      label: 'SKU',
                      value: (
                        <Link
                          to={`/inventory/${record.itemId}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {record.item.sku}
                        </Link>
                      ),
                    },
                    { label: 'Category', value: record.item.category },
                    { label: 'Unit cost', value: formatCurrency(record.unitCost ?? 0) },
                    {
                      label: 'Total cost',
                      value: formatCurrency(record.totalCost ?? 0),
                      tone: 'info',
                    },
                  ]}
                />
              </RailCard>

              <RailCard
                title="Where and who"
                icon={<MapPin className="h-4 w-4 text-muted-foreground" />}
              >
                <DetailList
                  items={[
                    {
                      label: 'Location',
                      value: (
                        <Link
                          to={`/locations/${record.locationId}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {record.location.name}
                        </Link>
                      ),
                    },
                    { label: 'Consumed by', value: record.consumedBy?.fullName ?? '—' },
                    { label: 'Consumed at', value: formatDateTime(record.consumedAt) },
                    {
                      label: 'Work order',
                      value: record.workOrder ? (
                        <Link
                          to={`/work-orders/${record.workOrderId}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {record.workOrder.workOrderNumber}
                        </Link>
                      ) : null,
                      when: !!record.workOrder,
                    },
                    {
                      label: 'Approved by',
                      value: record.approvedBy?.fullName,
                      when: !!record.approvedBy,
                    },
                  ]}
                />
              </RailCard>

              {(record.jobName || record.operationPhase) && (
                <RailCard title="Job" icon={<Gauge className="h-4 w-4 text-muted-foreground" />}>
                  <DetailList
                    items={[
                      { label: 'Job / well', value: record.jobName, when: !!record.jobName },
                      {
                        label: 'Phase',
                        value: record.operationPhase ? toTitleCase(record.operationPhase) : null,
                        when: !!record.operationPhase,
                      },
                      {
                        label: 'Rate',
                        value:
                          record.consumptionRate != null
                            ? `${record.consumptionRate.toFixed(3)} ${record.unit} ${record.rateUnit ?? ''}`.trim()
                            : null,
                        when: record.consumptionRate != null,
                      },
                      {
                        label: 'Base metric',
                        value: record.baseMetric != null ? formatNumber(record.baseMetric) : null,
                        when: record.baseMetric != null,
                      },
                    ]}
                  />
                </RailCard>
              )}
            </>
          }
          main={
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Variance</h3>
                  <CardDescription>
                    What was planned against what was actually drawn
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <StatStrip
                    stats={[
                      {
                        label: 'Planned',
                        value:
                          record.plannedQuantity != null
                            ? `${formatNumber(record.plannedQuantity)} ${record.unit}`
                            : '—',
                      },
                      {
                        label: 'Actual',
                        value: `${formatNumber(record.quantity)} ${record.unit}`,
                      },
                      {
                        label: 'Variance',
                        value:
                          variance != null ? (
                            // The sign carries the meaning: over-consumption
                            // costs money, under-consumption suggests the plan
                            // was wrong. A bare magnitude hides which.
                            <span
                              className={
                                Math.abs(variance) < 10
                                  ? ''
                                  : variance > 0
                                    ? 'text-status-destructive-foreground'
                                    : 'text-status-warning-foreground'
                              }
                            >
                              {variance > 0 ? '+' : ''}
                              {variance.toFixed(1)}%
                            </span>
                          ) : (
                            '—'
                          ),
                      },
                    ]}
                  />

                  {record.varianceReason && (
                    <div className="border-t pt-3">
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Reason given
                      </p>
                      <p className="text-sm text-muted-foreground">{record.varianceReason}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {returned > 0 && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Returned</h3>
                    <CardDescription>
                      Material backloaded against this record. The net figure is what was genuinely
                      consumed
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <StatStrip
                      stats={[
                        {
                          label: 'Drawn',
                          value: `${formatNumber(record.quantity)} ${record.unit}`,
                        },
                        { label: 'Returned', value: `${formatNumber(returned)} ${record.unit}` },
                        { label: 'Net used', value: `${formatNumber(net)} ${record.unit}` },
                      ]}
                    />
                    {record.returnReason && (
                      <div className="border-t pt-3">
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Return reason
                        </p>
                        <p className="text-sm text-muted-foreground">{record.returnReason}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {record.notes && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Notes</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{record.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          }
        />
      </motion.div>
    </motion.main>
  );
}
